import { AlertCircleIcon, ArrowRightIcon } from 'lucide-react'
import { useEffect } from 'react'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { CenteredLayout } from 'pages/employer_onboarding/CenteredLayout'
import { Button, usePageTitle } from 'slab'
import { usePostQuotingProspectsProspectIdQuotesUserConsent as useCreateUserConsent } from 'utils/web_api'

import { useQuote } from '../QuoteProvider'
import { useProspect } from './ProspectProvider'

interface PendingRatesPageProps {
  isReminderPage?: boolean
}

export function PendingRatesPage ({ isReminderPage = false }: PendingRatesPageProps) {
  usePageTitle('Pending rates')

  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()
  const { quote } = useQuote()
  const { prospect } = useProspect()

  const selectedYear = quote?.coverage_start_date
    ? new Date(quote.coverage_start_date).getUTCFullYear()
    : new Date().getFullYear() + 1

  const createUserConsentMutation = useCreateUserConsent({
    mutation: {
      onError: (error: GrapeAPIErrorType) => {
        console.error(error)
      }
    }
  })

  useEffect(() => {
    if (isReminderPage && prospect?.id) {
      createUserConsentMutation.mutate({ prospectId: prospect.id })
    }
  }, [isReminderPage, prospect?.id])

  const headerText = isReminderPage
    ? 'One more reminder...'
    : `Heads up for your ${selectedYear} start date`

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading={
          <div className='flex items-center justify-center gap-3'>
            <AlertCircleIcon
              color='#EAB308'
              size={20}
            />
            <h3 className='heading-xl'>{headerText}</h3>
          </div>
        }
        subheading="Prices for insurance plans in most states won't be finalized until the end of October. To plan for this, keep this timeline in mind:"
      />
      <div className='mt-10 flex justify-center'>
        <div className='flex'>
          <div className='flex flex-col items-center mr-4 mt-6'>
            <div className='w-2 h-2 bg-thatch-500 rounded-full' />
            <div className='w-px h-[95.75px] bg-thatch-400 mt-1.5 mb-1.5' />
            <div className='w-2 h-2 bg-thatch-500 rounded-full' />
            <div className='w-px h-[95.75px] bg-thatch-400 mt-1.5 mb-1.5' />
            <div className='w-2 h-2 bg-thatch-500 rounded-full' />
            <div className='w-px h-[95.75px] bg-thatch-400 mt-1.5 mb-1.5' />
            <div className='w-2 h-2 bg-thatch-500 rounded-full' />
          </div>
          <div className='space-y-4'>
            <div className='bg-white border border-gray-200 rounded-2xl p-4 w-96 shadow-[0_1px_4px_0_rgba(0,0,0,0.10)]'>
              <div className='body-bold mb-2'>Today</div>
              <span className='body'>
                We'll show your estimated healthcare costs based on <span className='body-bold'>projected</span> {selectedYear} rates.
              </span>
            </div>

            <div className='bg-white border border-gray-200 rounded-2xl p-4 w-96 shadow-[0_1px_4px_0_rgba(0,0,0,0.10)]'>
              <div className='body-bold mb-2'>During October</div>
              <span className='body'>
                We'll let you know once your team's rates are finalized (every state will vary).
              </span>
            </div>

            <div className='bg-white border border-gray-200 rounded-2xl p-4 w-96 shadow-[0_1px_4px_0_rgba(0,0,0,0.10)]'>
              <div className='body-bold mb-2'>Nov 4</div>
              <span className='body'>
                Your last day to review the finalized rates and adjust allowances.
              </span>
            </div>

            <div className='bg-white border border-gray-200 rounded-2xl p-4 w-96 shadow-[0_1px_4px_0_rgba(0,0,0,0.10)]'>
              <div className='body-bold mb-2'>Nov 5</div>
              <span className='body'>
                Open Enrollment begins for {selectedYear} and your employees can start enrolling in plans.
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className='mt-10'>
        <CenteredLayout.Buttons>
          <Button variant='secondary' onClick={navigateToPreviousStep}>
            Back
          </Button>
          <Button
            variant='primary'
            onClick={navigateToNextStep}
            rightIcon={ArrowRightIcon}
          >
            I understand, let's continue
          </Button>
        </CenteredLayout.Buttons>
      </div>
    </CenteredLayout>
  )
}
